import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import React, {useEffect, useState} from 'react';
import {NotificationManager} from 'react-notifications';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {StyledNewRewards} from './rewards.styled';
import {hideLoader, showLoader} from '../../store/common/slice';
import StringUtils from '../../common/utils/strings';
import coin from '../../images/newDesign/coin.svg';
import reward from '../../images/newDesign/reward.svg';
import bg from '../../images/newDesign/coub5.svg';
import info from '../../images/newDesign/info.svg';

import 'react-notifications/lib/notifications.css';
import {useAccessToken} from '../../common/hooks/useAccessToken';
import PrizesList from './PrizesList';
import { constants } from "../../common/constants/constants";

export default function RewardsNew() {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const userProfile = useSelector((state) => state.auth.userProfile);
    const ltr = useSelector((state) => state.common.ltrEnabled);

    const [campaign, setCampaign] = useState({});
    const [balance, setBalance] = useState({});
    const [selectedPrizeId, setSelectedPrizeId] = useState();

    const client = useAccessToken();

    useEffect(async () => {
        if (client != null && userProfile.userFirstName != null) {
            dispatch(showLoader());
            const partnerID = (userProfile.partnerID ?? '').toLowerCase();
            const shouldMock = constants.demoBuildingIDS.includes(partnerID);
            try {
                if (shouldMock){
                    await loadMockedCurrentCampaigns(partnerID);
                } else {
                    await loadCurrentCampaigns();
                }
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(hideLoader());
            }
        }
    }, [client, userProfile]);

    const loadCurrentCampaigns = async () => {
        const response = await client.get('/managers/rewards/campaigns/active');
        await setServerResponse(response);
    }

    const loadMockedCurrentCampaigns = async (partnerID) => {
        // load dynamically to exclude from common bundle
        const module = await import('../../common/mocks/specificBuildingsMockMapping');
        const getSpecificMockDataForPartner = module.getSpecificMockDataForPartner;
        const data = getSpecificMockDataForPartner(partnerID, 'notifications');
        if (data) {
            await setServerResponse(data);
        }else{
            await loadCurrentCampaigns();
        }

    }

    const setServerResponse = async (response) => {
        const campaigns = response?.campaigns || [];
        const coinBalance = response?.coinBalance || [];
        campaigns[0]?.Prizes?.length > 0 && campaigns[0].Prizes.sort((a, b) => a.coinValue - b.coinValue);
        setCampaign(campaigns[0] || {});
        setBalance(coinBalance[0]);
    }

    const getNextPrize = () => {
        if (!isPrizesExist()) return;
        return campaign.Prizes?.find((item, index) => {
            if (campaign.Prizes[index + 1] && (item.coinValue < balance?.coinValue && campaign.Prizes[index + 1] > balance?.coinValue)) {
                return campaign.Prizes[index + 1];
            }
            if (item.coinValue > balance?.coinValue) {
                return item;
            }
            return undefined;
        });
    }

    const isPrizesExist = () => {
        return campaign?.Prizes?.length > 0;
    }

    const collectPrize = async (rewardDetails = {}) => {
        dispatch(showLoader());

        let response;
        try {
            response = await client.managers.rewards.addReward(campaign.campaignId, selectedPrizeId, rewardDetails);
            await loadCurrentCampaigns();
            if (response.event) {
                NotificationManager.success(t('manager_rewards.prize_collect_success'), '', 7000);
            }
            response = true;
        } catch (e) {
            NotificationManager.error(t(`manager_rewards.${e.data.key}`), '', 7000);
            response = false;
        }

        dispatch(hideLoader());
        return response;
    }

    return (
        <StyledNewRewards className={'rewards'} ltr={ltr}>
            <h1 className={'page-title'}>
                <span>{t('manager_rewards.title')}</span>
                <span className='highlighted'>{userProfile.userFirstName}</span>
            </h1>

            <div className={'rewards-stats'}>
                <div className={'rewards-block'}>
                    <img className={'block-bg'} src={bg} alt="bg" />
                    <div className="block-wrap">
                        <div className='gift-logo'>
                            <img src={reward} alt="gift" />
                        </div>
                        <h3>{t('manager_rewards.coins_gained')}</h3>
                        <div className='total'>
                            <strong>{StringUtils.numberWithCommas(balance?.coinValue)}</strong>
                        </div>
                        { getNextPrize() && (
                            <span className="next-prize">{t('manager_rewards.next_prize', { 
                                returnObjects: true,
                                amountLeft: <span class='amount'>{StringUtils.numberWithCommas(getNextPrize().coinValue - balance.coinValue)}</span>
                            })}
                            </span>
                        )}
                    </div>
                </div>

                <div className={'rewards-block'}>
                    <div className="block-wrap cost">
                        <h3 className='block-title'>
                            {t('manager_rewards.actions_title')}
                            <OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {t('manager_rewards.collecting_points_tooltip')}
                                    </Tooltip>
                                )}
                            >
                                <img src={info} className='info' />
                            </OverlayTrigger>
                        </h3>
                        <div className='list-headers'>
                            <span>{t('manager_rewards.action')}</span>
                            <div>
                                <img src={coin} alt="coin" />
                            </div>
                        </div>
                        <ul className='list'>
                            {
                                campaign?.Rewards && campaign.Rewards.map(item => (
                                    <li key={item.campaignRewardId}>
                                        <span className="name">{item.EarningAction.name}</span>
                                        <span className="cost">{item.coinValue}</span>
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                </div>
            </div>

            <PrizesList
                prizes={campaign.Prizes}
                balance={balance}
                selectedPrizeId={selectedPrizeId}
                setSelectedPrizeId={setSelectedPrizeId}
                collectPrize={collectPrize}
                canCollect={isPrizesExist() && balance?.coinValue < campaign.Prizes[0].coinValue}
                ltr={ltr}
            />

        </StyledNewRewards>
    )
};
